import { css, html, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { animations, pixelCornersCss } from "../helpers/cssImports";
import { RegisteredUser, User, Username } from "@shared/types";
import { LoginRegisterBaseClass } from "./LoginRegisterBaseClass";

/** CSS affecting the {@link RegisterComponent} */
const style: string = css`
:host {
    font-family: "ka";
    width: 50%;
    display: flex;
    flex-direction: column;

}

.title {
    background-color: rgba(0, 0, 0);
    text-align: center;
    margin: 1%;
}

.register {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10%;
    background-color: rgba(0, 0, 0);
    text-align: center;
    margin: 1%;
    flex-grow: 1;
}

.username, .password {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2%;
    font-family: "pixel";
    font-size: 40px;
    width: 100%;
}

.username input, .password input {
    padding: 1%;
    width: 100%;
    border-color: rgb(15, 122, 237);
    font-size: 35px;
    font-family: "pixel";
    border-width: 8px;
}

form {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
}

label {
    margin-right: auto;
}

.message {
    font-family: "pixel";
    font-size: 30px;
    min-height: 80px;
    color: red;
}

button[type="submit"] {
    width: 100%;
    font-family: "pixel";
    font-size: 30px;
    margin: 10%;
    background-color: rgb(15, 122, 237);
    color: white;
    padding: 2%;
    transition: background-color 0.3s ease-in-out;
}

button[type="submit"]:hover {
    background-color: black;
    animation: hover 0.5s ease-in-out 2 forwards, glow 0.5s infinite;
    cursor: pointer;
}

.login {
    font-family: "pixel";
    font-size: 30px;
    text-decoration: underline;
    transition: color 0.3s ease-in-out;
}

.login:hover {
    color: rgb(15, 122, 237);
    animation: hover 0.5s ease-in-out 2 forwards;
    cursor: pointer;
}

p {
    font-family: "pixel";
    font-size: 30px;
} 

${pixelCornersCss}
${animations}
`;

/**
 * Represents the register web component
 *  Handles the register functionality
 */
export class RegisterComponent extends LoginRegisterBaseClass {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /**
     * Constructor of the web component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render();
    }

    /**
     * Renders the registration page UI
     * Includes register form, styling and setting up buttons
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const registerForm: HTMLElement = this.createRegisterForm();
        const elements: HTMLElement[] = htmlArray`
            <style>
                ${style}
            </style>
                ${this.createRegisterTitle()}
                ${registerForm}
        `;

        this.shadowRoot.append(...elements);
        this.setUpRegisterBtn(registerForm);
        this.setUpLogin(registerForm);
        localStorage.setItem("userId", "0");
        document.body.style.backgroundImage = "url('/backgrounds/login.gif')";
    }

    /**
     * Creates title for the register page
     * @returns - HTML element of the title seciton
     */
    private createRegisterTitle(): HTMLElement {
        const element: HTMLElement = html`
            <section class="title pixel-corners">
                <div>
                    <h2>Register</h2>
                </div>
            </section>
        `;

        return element;
    }

    /**
     * Creates the register form
     * Includes input for username, password and submit button
     * @returns  - The created register form HTML
     */
    private createRegisterForm(): HTMLElement {
        const element: HTMLElement = html`
            <section class="register pixel-corners">
            <p>Create an account and start playing!</p>
                <form>
                    <div class="username">
                        <label for="username">> Username </label>
                        <input type="text" id="username" name="username" class="pixel-corners">
                    </div>
                    <div class="password">
                    <label for="password">> Password</label>
                    <input type="password" id="password" name="password" class="pixel-corners">
                    </div>
                    <div class="password">
                        <label for="password">> Confirm password</label>
                        <input type="password" id="confirm-password" name="password" class="pixel-corners">
                    </div>
                    <button class="pixel-corners submit" type="submit">Register</button>
                </form>
                <div class="message"></div>
                <div class="login">Login</div>
            </section>
        `;

        return element;
    }

    /**
     * Sets up the register button event listener when clicked.
     * @param element - The element to set up
     */
    private setUpRegisterBtn(element: HTMLElement): void {
        const registerBtn: HTMLElement = element.querySelector(".submit") as HTMLElement;
        registerBtn.addEventListener("click", (event: Event) => this.handleRegisterBtnClick(element, event));
    }

    /**
     * Handles the click event of the register button
     * Validates input, if valid, handles the register
     * @param element - The register form
     * @param event - Event
     */
    private async handleRegisterBtnClick(element: HTMLElement, event: Event): Promise<void> {
        event.preventDefault();
        // Get the related elements
        const { username, password, confirmPassword, message } = this.getElements(element);

        try {
            // Validate the input
            if (!await this.validateInput(username, password, confirmPassword, message)) return;

            // If input is valid, register the user
            await this.handleRegister(username, password);

            // Sends the user to the canvas page
            this.handleSwitchPage(Page.Canvas);
        }
        catch {
            this.showMessage(message, "Something went wrong. try again.");
        }
    }

    /**
     * Retrieve HTML elements of input fields
     * @param element - The register form
     * @returns - username, password, confirm password values and the message element
     */
    private getElements(element: HTMLElement): {
        username: string;
        password: string;
        confirmPassword: string;
        message: HTMLElement;
    } {
        return {
            username: (element.querySelector("#username") as HTMLInputElement).value,
            password: (element.querySelector("#password") as HTMLInputElement).value,
            confirmPassword: (element.querySelector("#confirm-password") as HTMLInputElement).value,
            message: element.querySelector(".message") as HTMLElement,
        };
    }

    /**
     * Handles the register process
     * creates a new playersession, saves the playersession and sends the registration data to backend
     * @param username - The username to register
     * @param password - The password to register
     */
    private async handleRegister(username: string, password: string): Promise<void> {
        const playerSessionId: string = crypto.randomUUID();

        // Create a new user object and send it to the backend
        const user: User = this.createUser(username, password, playerSessionId);

        // Retrieve the just registered user
        const registeredUser: RegisteredUser[] = await this._gameRouteService.registerUser(user);

        // Update the local storage with the registered user
        localStorage.setItem("userId", String(registeredUser[0].id));
        localStorage.setItem("playerSessionId", playerSessionId);
        localStorage.setItem("isLoggedIn", "true");
    }

    /**
     * Validates the input fields for username, password and corfirm password.
     * @param username - Username input
     * @param password - Password input
     * @param confirmPassword - confirm password input
     * @param message - The message element to display validation message
     * @returns - True, if register is valid, false otherwise
     */
    private async validateInput(username: string, password: string, confirmPassword: string, message: HTMLElement): Promise<boolean> {
        // Check for any empty fields
        if (!username || !password || !confirmPassword) {
            this.showMessage(message, "Please enter valid information.");
            return false;
        }

        // Validate username and password
        if (!await this.validateUsername(username, message)) return false;
        if (!this.validatePassword(password, confirmPassword, message)) return false;

        return true;
    }

    /**
     * Handles the validation of the provided password.
     * @param password - The provided password of the user
     * @param confirmPassword - The provided confirm password of the user
     * @param message - The message element to display the message in
     * @returns True if password is valid. false otherwise.
     */
    private validatePassword(password: string, confirmPassword: string, message: HTMLElement): boolean {
        // Check if the password is valid
        const passwordRegex: RegExp = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
        if (!passwordRegex.test(password)) {
            this.showMessage(
                message, "Password must have atleast 8 characters, at least one letter and one number.");
            return false;
        }

        // Check if the password and confirm password fields are the same
        if (password !== confirmPassword) {
            this.showMessage(message, "Passwords do not match.");
            return false;
        }
        return true;
    }

    /**
     * Validates the provided username
     * @param username - The provided username the user put in
     * @param message - The message element to display the message in.
     * @returns True if username is valid, false otherwise.
     */
    private async validateUsername(username: string, message: HTMLElement): Promise<boolean> {
        // Check if the username is valid
        const usernameRegex: RegExp = /^[a-zA-Z][a-zA-Z0-9_]{2,15}$/;
        if (!usernameRegex.test(username)) {
            this.showMessage(message, "Please enter a valid username.");
            return false;
        }

        // Check if username already exists in the db
        const createdUsername: Username = { username: username };
        const existingUser: Username[] = await this._gameRouteService.getExistingUsername(createdUsername);
        if (existingUser.length !== 0) {
            this.showMessage(message, "Username is already taken.");
            return false;
        }

        return true;
    }

    /**
     * Sets up the login button to navigate the user to the login page
     * @param element - The sign up button
     */
    private setUpLogin(element: HTMLElement): void {
        const login: HTMLElement = element.querySelector(".login") as HTMLElement;
        login.addEventListener("click", () => this.handleSwitchPage(Page.Login));
    }
}
